// ─────────────────────────────────────────────────────────────────────────────
// API message map — every known English message string each backend container
// (detector) can emit into its output payload, grouped by detector key.
//
// Fields the frontend reads from the API and runs through resolveApiMessage():
//   - det.message      (when det.status === "error")  → all detectors
//   - det.errors[0]    (dns)
//   - det.msg          (urlcheck when is_live=false, sitecrawle, whois)
//
// Matching uses startsWith, so an entry's `text` is the STABLE PREFIX.
// Any dynamic remainder (URL / IP / raw exception) is preserved and appended
// after the translated prefix — so we localize the label without losing detail.
//
// To add a new message: add an entry here + the same key under
// "apiMessages.<container>" in en.json AND ja.json.
//
// NOTE: pure `str(e)` exception messages are unpredictable and can't be mapped;
// they fall through to apiMessages.common.unknownErrorFallback.
// ─────────────────────────────────────────────────────────────────────────────

export const API_MESSAGE_MAP = {
  // urlcheck — URL response inspection (build/urlcheck/urlcheck/consumer.py)
  urlcheck: [
    { text: "The URL not alive", key: "urlNotAlive" },
  ],

  // sitecrawle — website crawling (build/sitecrawle/sitecrawle/consumer.py)
  sitecrawle: [
    { text: "Cannot get the website content of domain", key: "cannotGetContent" },
  ],

  // dns — DNS infrastructure (build/dns + build/dig_lambda)
  dns: [
    { text: "No A record found", key: "noARecord" },
    { text: "Dig lookup failed", key: "digLookupFailed" },
    { text: "Missing domain", key: "missingDomain" },
    { text: "Invalid domain format", key: "invalidDomainFormat" },
    { text: "Forward dig error: ", key: "forwardDigError" },
    { text: "Forward dig failed: ", key: "forwardDigFailed" },
    { text: "Invalid IP address (skipped reverse): ", key: "invalidIpSkipped" },
    { text: "Reverse dig error for ", key: "reverseDigError" },
    { text: "Reverse dig failed for ", key: "reverseDigFailed" },
    { text: "Lambda call failed: ", key: "lambdaCallFailed" },
  ],

  // whois — domain registration analysis (build/whois + build/whois_lambda)
  whois: [
    { text: "Domain older than 9 months", key: "domainOlderThan9Months" },
    { text: "WHOIS call failed: ", key: "whoisCallFailed" },
    { text: "Missing domain", key: "missingDomain" },
    { text: "Invalid domain format", key: "invalidDomainFormat" },
    { text: "Internal server error", key: "internalServerError" },
  ],

  // whitecheck — reputation verification (build/whitecheck)
  // Only generic error path active; covered by common.
  whitecheck: [],

  // urldet — address spoofing inspection (build/urldet)
  // Only generic error path (status=error + str(e)); covered by common.
  urldet: [],

  // htmldet — page content / form structure (build/htmldet)
  // Only generic error path; covered by common.
  htmldet: [],

  // visual_branddet — visual brand imitation (build/visualdet_branddet)
  visual_branddet: [
    { text: "Brand Detection Processing Error", key: "brandDetectionError" },
  ],

  // visual_logodet — visual logo imitation (build/visualdet_logodet)
  visual_logodet: [
    { text: "Logo Detection Processing Error", key: "logoDetectionError" },
  ],

  // meta_analysis — site characteristics (build/meta_worker)
  meta_analysis: [
    { text: "metadata is not a dict", key: "metadataNotDict" },
  ],

  // Shared fallbacks used across all detectors
  common: [
    { text: "Detector not available", key: "detectorNotAvailable" },
    { text: "Unknown error", key: "unknownError" },
  ],
};

/**
 * Resolve an API message string to a translated string.
 *
 * 1. Look up detKey-specific entries first, then "common".
 * 2. On match: translate the stable prefix, then append any dynamic remainder
 *    (e.g. the URL/IP that followed the prefix) unchanged.
 * 3. No match: EN shows the raw message; JP shows a generic wrapper + raw text.
 *
 * @param {string} msg    - raw English message from the API
 * @param {string} detKey - detector key (e.g. "urlcheck", "dns", "visual_branddet")
 * @param {function} t    - i18next t() function
 */
export function resolveApiMessage(msg, detKey, t) {
  if (!msg || msg === "—") return msg;

  const detEntries = API_MESSAGE_MAP[detKey] ?? [];
  const commonEntries = API_MESSAGE_MAP.common ?? [];

  const detHit = detEntries.find((e) => msg.startsWith(e.text));
  const hit = detHit ?? commonEntries.find((e) => msg.startsWith(e.text));

  if (hit) {
    const ns = detHit ? detKey : "common";
    const translated = t(`ui.apiMessages.${ns}.${hit.key}`, hit.text);
    const remainder = msg.slice(hit.text.length); // dynamic tail (URL/IP/exception)
    return remainder ? `${translated}${remainder}` : translated;
  }

  // No mapping — show a generic message (EN: "Internal Server Error")
  return t("ui.apiMessages.common.internalServerError", "Internal Server Error");
}
